import type { ApiResource } from '../schema';

export const resource: ApiResource = {
  id: 'auth-tenancy',
  slug: 'auth-tenancy',
  name: 'Auth & Tenancy',
  description: 'Single sign-on entry points, mobile trusted-device enrollment, the current tenant\'s own profile, the subscription plan catalog, and public self-serve trial signup.',
  icon: 'AdminPanelSettings',
  endpoints: [
    {
      slug: 'sso-google-start',
      method: 'GET',
      path: '/api/auth/google/start',
      summary: 'Start Google SSO sign-in',
      description: 'Browser-redirect entry point for Google (OpenID Connect) sign-in — not a JSON API call. Visiting this URL 302-redirects the browser to Google\'s consent screen with a freshly minted, short-lived CSRF state; Google later redirects back to a callback route, which exchanges the code, verifies the profile, and issues a normal session cookie. Requires Google SSO to already be configured for the tenant; returns 400 with a plain-text message otherwise.',
      auth: 'public',
      responseExample: `{
  "status": 302,
  "location": "https://accounts.google.com/o/oauth2/v2/auth?client_id=...&redirect_uri=...&response_type=code&scope=openid+email+profile&state=9f2a...&prompt=select_account"
}`,
      sourceFile: 'routes/sso.js',
    },
    {
      slug: 'sso-microsoft-start',
      method: 'GET',
      path: '/api/auth/ms/start',
      summary: 'Start Microsoft Entra ID SSO sign-in',
      description: 'Browser-redirect entry point for Microsoft Entra ID (Azure AD) sign-in, mirroring the Google flow: a 302 redirect to the tenant\'s Entra authorize endpoint with a short-lived CSRF state. The callback cryptographically verifies the returned id_token\'s signature against Entra\'s JWKS, audience, and issuer/tenant before ever matching it to a local account.',
      auth: 'public',
      responseExample: `{
  "status": 302,
  "location": "https://login.microsoftonline.com/<tenant-id>/oauth2/v2.0/authorize?client_id=...&redirect_uri=...&response_type=code&response_mode=query&scope=openid+email+profile&state=6d10...&prompt=select_account"
}`,
      sourceFile: 'routes/mssso.js',
    },
    {
      slug: 'trusted-device-enroll',
      method: 'POST',
      path: '/api/trusted-devices/enroll',
      summary: 'Enroll a trusted mobile device',
      description: 'Mints a long-lived device token for the currently signed-in admin, letting a mobile client skip SSO + 2FA on later app opens. Requires an already-fully-authenticated admin session — the convenience credential is never issued without it. The token is shown once, in this response; the client trades it for a fresh session later via POST /api/trusted-devices/exchange (public, no session needed). GET /api/trusted-devices lists the caller\'s own enrolled devices; DELETE /api/trusted-devices/:id revokes one immediately.',
      auth: 'session',
      requestBodyExample: `{ "label": "Micheal's iPhone", "platform": "ios" }`,
      responseExample: `{
  "token": "<one-time-device-token>",
  "deviceId": "dvc_51ac",
  "label": "Micheal's iPhone",
  "idleDays": 30,
  "absoluteDays": 180,
  "absoluteExpiresAt": "2027-01-17T14:10:00.000Z"
}`,
      sourceFile: 'routes/trusted-devices.js, lib/device-tokens.js',
    },
    {
      slug: 'tenant-get',
      method: 'GET',
      path: '/api/tenant',
      summary: 'Get the current tenant',
      description: 'Returns the authenticated caller\'s own tenant — id, name, slug, whether it\'s the primary/default tenant, its status, whether the caller is a super-admin (an admin operating on the default tenant), and whether the current session is a cross-tenant impersonation view.',
      auth: 'session',
      responseExample: `{
  "id": "tnt_stedward",
  "name": "St. Edward School",
  "slug": "stedward",
  "default": false,
  "status": "active",
  "super": false,
  "impersonating": false
}`,
      sourceFile: 'routes/tenants.js',
    },
    {
      slug: 'plan-catalog',
      method: 'GET',
      path: '/api/plan/catalog',
      summary: 'Get the subscription plan catalog',
      description: 'Returns the active entries in the platform\'s plan catalog — key, display name, monthly/annual price, trial length, and the plan\'s quota figures — for a tenant admin comparing tiers. This endpoint only returns the catalog; it does not enforce or gate anything. Quota and trial-window checks elsewhere in the platform are advisory notices on the current build, not access blocks.',
      auth: 'session',
      responseExample: `[
  { "key": "starter", "name": "Starter", "priceMonthly": 149, "priceAnnual": 1490, "trialDays": 14, "quotas": { "devices": 100, "seats": 5 } },
  { "key": "growth", "name": "Growth", "priceMonthly": 349, "priceAnnual": 3490, "trialDays": 14, "quotas": { "devices": 500, "seats": 20 } }
]`,
      sourceFile: 'routes/plan-billing.js, lib/plans.js',
    },
    {
      slug: 'public-signup',
      method: 'POST',
      path: '/api/public/signup',
      summary: 'Request a self-serve trial',
      description: 'Public, unauthenticated intake for a self-serve trial request — creates a PENDING review record, never a tenant. An admin with tenant-registry rights approves or rejects the request from the review queue; approval provisions a real tenant on the same code path as a manual admin-created tenant and starts the plan\'s trial window. Off by default per tenant (returns 404 until an admin enables it) and rate-limited; a hidden honeypot field silently accepts obvious bot submissions without creating a request.',
      auth: 'public',
      requestBodyExample: `{ "orgName": "Example Charter School", "adminEmail": "it@examplecharter.org", "planKey": "starter" }`,
      responseExample: `{ "ok": true, "id": "sgn_c40b" }`,
      sourceFile: 'routes/signup.js, lib/signups.js',
    },
  ],
};
